// ABOUTME: Reusable single-select datagrid filter driven by params (RTK query hook + backend key)
// ABOUTME: Renders the query's select options as a toggleable list writing option.id to the filter
import { Box, List, ListItem, ListItemButton, useTheme } from '@mui/material';
import { tokens } from '../../../../../theme';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useGridFilter } from '../../../../../contexts/GridFilterContext';
import { updateFilter } from '../../../../../newapi/global/filter/gridFilterSlice';
import Text from '../../../../theme/text/Text';

/**
 * GeneralSelectFilter
 *
 * A generic single-select filter that sources its options from any RTK Query
 * select endpoint. Configure it entirely through the filter key's `params`:
 *   {
 *     query: useSomeSelectQuery,   // RTK Query hook, called with queryArg
 *     backendKey: "someId",        // filter key sent to the backend
 *     displayName: "name",         // option property rendered as the label (default "name")
 *     valueKey: "id",              // option property stored as the value (default "id")
 *     queryArg: undefined,         // optional argument passed to the query hook
 *     dataKey: undefined,          // property holding the rows when the endpoint wraps them, e.g. "data"
 *   }
 */
const GeneralSelectFilter = ({ params }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const filterKey = params.backendKey;
    const displayName = params.displayName ?? "name";
    const valueKey = params.valueKey ?? "id";

    const { data: response } = params.query(params.queryArg);
    const options = params.dataKey ? response?.[params.dataKey] : response;

    const { index, id } = useGridFilter();
    const filterObject = useSelector((state) => {
        const instance = state.gridFilter.viewInstances[index];
        return instance ? instance[id] : undefined;
    });

    const handleToggle = (itemValue) => {
        if (filterObject && filterObject.filter) {
            const currentValue = filterObject.filter[filterKey] ?? -1;
            const updatedValue = (currentValue === itemValue) ? -1 : itemValue;
            dispatch(updateFilter({ index, id, filterKey, value: updatedValue }));
        }
    };

    return (
        <Box
            sx={{
                display:"flex",
                flexDirection:"column",
                gap:1.5,
            }}
        >
            <List
                sx={{
                    padding:0,
                    display:"flex",
                    flexDirection:"column",
                    gap:1.5,
                }}
            >
                {options?.map((option, optionIndex) => (
                    <ListItem key={option[valueKey] + "index" + optionIndex} disablePadding
                        onClick={() => handleToggle(option[valueKey])}
                    >
                        <ListItemButton
                            className={cx({
                                active: filterObject && filterObject.filter?.[filterKey] === option[valueKey]
                            })}
                            sx={{
                                minWidth:250,
                                color:colors.txt["secondary"],
                                display:"flex",
                                justifyContent:"flex-start",
                                alignItems:"flex-start",
                                flexDirection:"column",
                                fontSize:"14px",
                                pl:2,
                                pr:0.5,
                                borderRadius:5,
                                background:theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[0],
                                border: theme.palette.mode === "dark" ? null : `1px solid ${colors.grey[100]}`,
                                boxShadow:theme.palette.mode === "dark" ? null :  `0px 2px 4px ${colors.grey[200]}`,
                                "&.active" : {
                                    background:theme.palette.mode === "dark" ? colors.blue[100] : colors.primary[100],
                                    color:theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400],
                                }
                            }}
                        >
                            <Text bold>{params.useTranslation ? t(option[displayName]) : option[displayName]}</Text>
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </Box>
    );
};

export default GeneralSelectFilter;
